import styled, { css, keyframes } from 'styled-components';

import { media } from 'src/styles/breakpoints';

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(calc(100% + 1.5rem)); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOut = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(calc(100% + 1.5rem)); }
`;

// On mobile the toast sits at the bottom, so it leaves the way it arrived.
const slideInUp = keyframes`
  from { opacity: 0; transform: translateY(calc(100% + 1.5rem)); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideOutDown = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(calc(100% + 1.5rem)); }
`;

// The progress bar IS the timer: when this animation ends, the toast closes.
// Pausing it (hover / focus) pauses the countdown exactly where it was.
const countdown = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;

export const Toast = styled.div<{ $isSuccess: boolean; $isLeaving: boolean }>`
  position: fixed;
  top: 7.5rem;
  right: 1.75rem;
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 22rem;
  max-width: calc(100vw - 2rem);
  padding: 1rem 1rem 1.25rem;
  border-radius: 0.75rem;
  overflow: hidden;
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.grey};
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.12),
    0 2px 6px rgba(0, 0, 0, 0.06);
  color: ${({ theme }) => theme.dark};
  animation: ${({ $isLeaving }) =>
    $isLeaving
      ? css`
          ${slideOut} 0.25s ease-in forwards
        `
      : css`
          ${slideIn} 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2)
        `};

  ${media.md} {
    top: auto;
    bottom: max(1rem, env(safe-area-inset-bottom));
    left: 1rem;
    right: 1rem;
    width: auto;
    max-width: none;
    animation: ${({ $isLeaving }) =>
      $isLeaving
        ? css`
            ${slideOutDown} 0.25s ease-in forwards
          `
        : css`
            ${slideInUp} 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2)
          `};
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.01ms;
  }
`;

export const ToastIcon = styled.span<{ $isSuccess: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  font-weight: 700;
  background: ${({ theme, $isSuccess }) => ($isSuccess ? theme.positive90 : theme.negative90)};
  color: ${({ theme, $isSuccess }) => ($isSuccess ? theme.positive : theme.negative)};

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

export const ToastMessage = styled.div`
  flex: 1;
  min-width: 0;
  align-self: center;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.45;
  overflow-wrap: anywhere;

  &::first-letter {
    text-transform: capitalize;
  }
`;

export const ToastClose = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  margin: -0.125rem -0.25rem 0 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.midGrey};
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.lightGrey};
    color: ${({ theme }) => theme.dark};
    outline: none;
  }
`;

export const ToastProgress = styled.span<{
  $isSuccess: boolean;
  $duration: number;
  $paused: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  transform-origin: left;
  background: ${({ theme, $isSuccess }) => ($isSuccess ? theme.positive : theme.negative)};
  animation: ${countdown} ${({ $duration }) => $duration}ms linear forwards;
  animation-play-state: ${({ $paused }) => ($paused ? 'paused' : 'running')};
`;
